import { useCallback, useEffect, useState } from "react";
import "./permission.scss";
import { Button, Flex, Form, Input, Radio, Table } from "antd";
import { RoleServices } from "../../services/role";
import {
  IBodyRoleDto,
  IModulePermission,
  IModulePermissionsResponse,
  IRoleDetailResponse,
  IRoleResult,
} from "../../interfaces/role";
import type { ColumnsType } from "antd/es/table";
import { useMessageApi } from "../../context/MessageContext";
import { useAuth } from "../../context/AuthContext";
import { UserServices } from "../../services/user";
import { IUserData } from "../../interfaces/auth";

interface CreateAndEditRoleProps {
  onClose: (okTriggered?: boolean) => void;
  detailRole?: IRoleResult | undefined;
}

const CreateAndEditRole: React.FC<CreateAndEditRoleProps> = ({
  onClose,
  detailRole,
}) => {
  const messageApi = useMessageApi();
  const [form] = Form.useForm();
  const [modulePermissions, setModulePermissions] = useState<
    IModulePermission[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);

  const [selectedPermissions, setSelectedPermissions] = useState<
    Record<string, string>
  >({});

  const isEdit = !!detailRole;
  const { user, setUser } = useAuth();
  const { roles = [] } = user || {};

  const buildInitialSelectedPermissions = useCallback(
    (modules: IModulePermission[], detail?: IRoleResult) => {
      const detailMap = (detail?.permissions || []).reduce((acc, cur) => {
        acc[cur.moduleName] = cur.name;
        return acc;
      }, {} as Record<string, string>);

      return modules.reduce((acc, cur) => {
        acc[cur.module] = detailMap[cur.module] ?? cur.permissions[0].name;
        return acc;
      }, {} as Record<string, string>);
    },
    []
  );

  useEffect(() => {
    form.setFieldsValue({ name: detailRole?.name || "" });
    const fetchData = async () => {
      try {
        setModulePermissions([]);
        setLoading(true);
        const response: IModulePermissionsResponse =
          await RoleServices.getModulePermissions();
        if (response.success) {
          setModulePermissions(response.data);

          setSelectedPermissions(
            buildInitialSelectedPermissions(response.data, detailRole)
          );
        }
      } catch (error) {
        console.error("Fetch roles error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [buildInitialSelectedPermissions, detailRole, form]);

  const handleChange = (module: string, permission: string) => {
    setSelectedPermissions({
      ...selectedPermissions,
      [module]: permission,
    });
  };

  useEffect(() => {
    form.setFieldsValue({ name: detailRole?.name || "" });
  }, [detailRole, form]);

  const columns: ColumnsType<IModulePermission> = [
    {
      title: "Permission",
      dataIndex: "moduleName",
      key: "moduleName",
    },
    {
      title: "None",
      key: "none",
      align: "center",
      render: (_: any, record: IModulePermission) => (
        <Radio
          checked={
            selectedPermissions[record.module] === record.permissions[0].name
          }
          defaultChecked={true}
          onChange={() =>
            handleChange(record.module, record.permissions[0].name)
          }
        />
      ),
    },
    {
      title: "Viewer",
      key: "viewer",
      align: "center",
      render: (_: any, record: IModulePermission) => (
        <Radio
          checked={
            selectedPermissions[record.module] === record.permissions[1].name
          }
          onChange={() =>
            handleChange(record.module, record.permissions[1].name)
          }
        />
      ),
    },
    {
      title: "Editor",
      key: "editor",
      align: "center",
      render: (_: any, record: IModulePermission) => (
        <Radio
          checked={
            selectedPermissions[record.module] === record.permissions[2].name
          }
          onChange={() =>
            handleChange(record.module, record.permissions[2].name)
          }
        />
      ),
    },
  ];

  const onFinish = async (values: any) => {
    const payload: IBodyRoleDto = {
      name: values.name.trim(),
      permissions: Object.values(selectedPermissions).filter(
        (value) => !value.endsWith("_NONE")
      ),
    };

    try {
      setIsLoadingSubmit(true);
      const response: IRoleDetailResponse = isEdit
        ? await RoleServices.updateRole(detailRole.id, payload)
        : await RoleServices.createRole(payload);
      const { success, message } = response;
      if (success) {
        onClose(true);
        messageApi.success(message);
        if (
          isEdit &&
          roles.map((role) => role.name).includes(detailRole.name)
        ) {
          fetchUser();
        }
      }
    } catch (error: any) {
      messageApi.error(error?.message);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const fetchUser = async () => {
    try {
      const res = (await UserServices.getMe()) as unknown as {
        success: boolean;
        data: IUserData;
      };
      if (res.success) {
        setUser(res.data);
      }
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} layout="horizontal" onFinish={onFinish}>
      <Form.Item
        label="Role Name"
        name="name"
        rules={[
          { required: true, message: "" },
          {
            validator: (_, value) =>
              value && value.trim() !== ""
                ? Promise.resolve()
                : Promise.reject("Complete this required field."),
          },
        ]}
      >
        <Input placeholder="Enter role name" />
      </Form.Item>
      <Table
        dataSource={modulePermissions}
        loading={loading}
        rowKey={(record) => record.module}
        columns={columns}
        pagination={false}
        size="small"
        className="table-fm"
        rowClassName={(record, index) =>
          index % 2 === 0 ? "highlight-row" : ""
        }
      />
      <Flex justify="end" gap={16} style={{ marginTop: 20 }}>
        <Button
          style={{ marginLeft: 8 }}
          onClick={() => onClose()}
          disabled={isLoadingSubmit}
          color="primary"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button type="primary" htmlType="submit" loading={isLoadingSubmit}>
          Save
        </Button>
      </Flex>
    </Form>
  );
};

export default CreateAndEditRole;
